/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';

import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { IconButtonProps } from '@atlaskit/button/icon/button';
import SidebarCollapseIcon from '@atlaskit/icon/core/sidebar-collapse';
import SidebarExpandIcon from '@atlaskit/icon/core/sidebar-expand';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { useOpenLayerObserver } from '@atlaskit/layering/use-open-layer-observer';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { useIsFhsEnabled } from '../../fhs-rollout/use-is-fhs-enabled';
import { IconButton } from '../../top-nav-items/themed/icon-button';
import { SideNavToggleButtonAttachRef } from './side-nav-toggle-button-attach-ref';
import { sideNavToggleTooltipKeyboardShortcut } from './side-nav-toggle-tooltip-keyboard-shortcut';
import { SideNavVisibilityState } from './side-nav-visibility-state';
import { useIsSideNavShortcutEnabled } from './use-is-side-nav-shortcut-enabled';
import { useSideNavVisibility } from './use-side-nav-visibility';
import { useToggleSideNav } from './use-toggle-side-nav';

export type SideNavVisibilityChangeAnalyticsAttributes = {
	isSideNavVisible: boolean;
};

const toggleButtonTooltipOptions: IconButtonProps['tooltip'] = {
	// We're disabling pointer events on the tooltip to prevent it from blocking mouse events, so that the side nav flyout stays open
	// when moving the mouse from the top bar to the side nav.
	ignoreTooltipPointerEvents: true,
	// A press swaps the label between `collapseLabel` and `expandLabel`, so keep the
	// tooltip open to show the new label.
	hasNewContentOnTriggerClick: true,
};

// For duplicate "mouseenter" issue when changing icons (see below)
const silentIconStyles = css({
	// So we don't mess up any flex logic inside of button
	display: 'contents',
	// Don't let movement over icons be relevant for events
	pointerEvents: 'none',
});

/**
 * __SideNavToggleButton__
 *
 * Button for toggling the side nav. It should be used in the top bar.
 */
export const SideNavToggleButton: ({
	defaultCollapsed,
	expandLabel,
	collapseLabel,
	testId,
	interactionName,
	onClick,
}: {
	/**
	 * @deprecated
	 *
	 * This prop is being replaced by `defaultSideNavCollapsed` on the `Root` element,
	 * and will be removed in the future.
	 *
	 * ---
	 *
	 * Whether the side nav should be collapsed by default __on desktop screens__.
	 *
	 * It is always collapsed by default for mobile screens.
	 *
	 * __Note:__ If using this prop, ensure that it is also provided to the `SideNav` slot.
	 * This is to ensure the state is in sync before post-SSR hydration.
	 */
	defaultCollapsed?: boolean;
	/**
	 * The label when the toggle button will expand the side nav.
	 */
	expandLabel: React.ReactNode;
	/**
	 * The label when the toggle button will collapse the side nav.
	 */
	collapseLabel: React.ReactNode;
	/**
	 * A unique string that appears as data attribute `data-testid` in the rendered code, serving as a hook for automated tests.
	 */
	testId?: string;
	/**
	 * An optional name used to identify events for [React UFO (Unified Frontend Observability) press interactions](https://developer.atlassian.com/platform/ufo/react-ufo/react-ufo/getting-started/#quick-start--press-interactions). For more information, see [React UFO integration into Design System components](https://go.atlassian.com/react-ufo-dst-integration).
	 */
	interactionName?: string;
	/**
	 * The callback function that is called when the toggle button is clicked.
	 */
	onClick?: (
		e: React.MouseEvent<HTMLElement>,
		analyticsEvent: UIAnalyticsEvent,
		attributes?: SideNavVisibilityChangeAnalyticsAttributes,
	) => void;
}) => JSX.Element = ({
	defaultCollapsed = false,
	expandLabel,
	collapseLabel,
	testId,
	interactionName,
	onClick,
}: {
	/**
	 * @deprecated
	 *
	 * This prop is being replaced by `defaultSideNavCollapsed` on the `Root` element,
	 * and will be removed in the future.
	 *
	 * ---
	 *
	 * Whether the side nav should be collapsed by default __on desktop screens__.
	 *
	 * It is always collapsed by default for mobile screens.
	 *
	 * __Note:__ If using this prop, ensure that it is also provided to the `SideNav` slot.
	 * This is to ensure the state is in sync before post-SSR hydration.
	 */
	defaultCollapsed?: boolean;
	/**
	 * The label when the toggle button will expand the side nav.
	 */
	expandLabel: React.ReactNode;
	/**
	 * The label when the toggle button will collapse the side nav.
	 */
	collapseLabel: React.ReactNode;
	/**
	 * A unique string that appears as data attribute `data-testid` in the rendered code, serving as a hook for automated tests.
	 */
	testId?: string;
	/**
	 * An optional name used to identify events for [React UFO (Unified Frontend Observability) press interactions](https://developer.atlassian.com/platform/ufo/react-ufo/react-ufo/getting-started/#quick-start--press-interactions). For more information, see [React UFO integration into Design System components](https://go.atlassian.com/react-ufo-dst-integration).
	 */
	interactionName?: string;
	/**
	 * The callback function that is called when the toggle button is clicked.
	 */
	onClick?: (
		e: React.MouseEvent<HTMLElement>,
		analyticsEvent: UIAnalyticsEvent,
		attributes?: SideNavVisibilityChangeAnalyticsAttributes,
	) => void;
}) => {
	const isFhsEnabled = useIsFhsEnabled();

	const {
		isExpandedOnDesktop: isSideNavExpandedOnDesktop,
		isExpandedOnMobile: isSideNavExpandedOnMobile,
	} = useSideNavVisibility({ defaultCollapsed });

	const sideNavState = useContext(SideNavVisibilityState);

	// When default state is provided to `Root` the state in context will already be
	// initialized in SSR
	const [isSideNavExpanded, setIsSideNavExpanded] = useState<boolean>(
		sideNavState === null ? !defaultCollapsed : isSideNavExpandedOnDesktop,
	);

	const ref = useContext(SideNavToggleButtonAttachRef);

	/**
	 * Addresses HOT-121458 by ensuring that the toggle button element in context is always up to date.
	 *
	 * My theory is that something to do with SSR, hydration and suspense was causing the
	 * underlying HTML element to change but without causing the toggle button to remount.
	 *
	 * This meant the effect calling `ref()` did not re-run, and the value in context became stale.
	 */
	const [element, setElement] = useState<HTMLButtonElement | null>(null);
	useEffect(() => {
		ref(element);
	}, [element, ref]);

	useEffect(() => {
		const { matches } = window.matchMedia('(min-width: 64rem)');
		setIsSideNavExpanded(matches ? isSideNavExpandedOnDesktop : isSideNavExpandedOnMobile);
	}, [isSideNavExpandedOnDesktop, isSideNavExpandedOnMobile]);

	useEffect(() => {
		// When screen size changes, ensure we use the correct visibility state
		const mediaQueryList = window.matchMedia('(min-width: 64rem)');
		return bind(mediaQueryList, {
			type: 'change',
			listener() {
				setIsSideNavExpanded(
					mediaQueryList.matches ? isSideNavExpandedOnDesktop : isSideNavExpandedOnMobile,
				);
			},
		});
	}, [isSideNavExpandedOnDesktop, isSideNavExpandedOnMobile]);

	const toggleVisibility = useToggleSideNav({ trigger: 'toggle-button' });

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const openLayerObserver = useOpenLayerObserver();

	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>, analyticsEvent: UIAnalyticsEvent) => {
			onClick?.(event, analyticsEvent, { isSideNavVisible: isSideNavExpanded });

			toggleVisibility();

			if (isFhsEnabled || fg('platform-dst-keep-desired-fhs-features')) {
				openLayerObserver?.closeLayers();
			}
		},
		[isFhsEnabled, isSideNavExpanded, onClick, openLayerObserver, toggleVisibility],
	);

	const handlePointerEnter = useCallback(() => {
		// Hovers don't do anything on mobile, so not capturing
		const isDesktop = window.matchMedia('(min-width: 64rem)').matches;
		if (!isDesktop) {
			return;
		}

		const navigationAnalyticsEvent = createAnalyticsEvent({
			source: 'topNav',
			actionSubject: 'sideNav',
			action: 'hovered',
			actionSubjectId: 'sideNavButton',
			attributes: {
				itemState: isSideNavExpanded ? 'expanded' : 'collapsed',
				screen: 'desktop',
			},
		});

		navigationAnalyticsEvent.fire('navigation');
	}, [createAnalyticsEvent, isSideNavExpanded]);

	/**
	 * ## Behaviour
	 * It is intentional that collapse icon will be used while the flyout is open.
	 * The icon is tied to the expanded / collapse state, and not the flyout state.
	 *
	 * ## Why a function?
	 * Unfortunately, changing the icon inside an <IconButton> when the user is over
	 * the button will cause the svg element to be replaced, which can trigger a
	 * "mouseenter" event. This is problematic when the user is already over the button,
	 * as it can result in a "mouseenter" event after the user manually entered the button.
	 *
	 * `icon` accepts a function for the `icon` prop (ie a render prop), so we don't need to
	 * memoize it, or pull it out into a new function
	 */
	const icon = (props: NewCoreIconProps) => (
		<span css={silentIconStyles}>
			{/* eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props */}
			{isSideNavExpanded ? <SidebarCollapseIcon {...props} /> : <SidebarExpandIcon {...props} />}
		</span>
	);

	const isShortcutEnabled = useIsSideNavShortcutEnabled();

	const tooltipProps = useMemo(() => {
		if (isFhsEnabled || fg('platform-dst-keep-desired-fhs-features')) {
			return {
				...toggleButtonTooltipOptions,
				shortcut: isShortcutEnabled ? sideNavToggleTooltipKeyboardShortcut : undefined,
			};
		}

		return toggleButtonTooltipOptions;
	}, [isFhsEnabled, isShortcutEnabled]);

	return (
		<IconButton
			appearance="subtle"
			label={isSideNavExpanded ? collapseLabel : expandLabel}
			icon={icon}
			onClick={handleClick}
			onPointerEnter={handlePointerEnter}
			testId={testId}
			isTooltipDisabled={false}
			interactionName={interactionName}
			ref={setElement}
			tooltip={tooltipProps}
		/>
	);
};
